import React, { useEffect, useState } from "react";
import ReactPDF, { PDFViewer } from "@react-pdf/renderer";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { GetSingleWingFrom } from "../../redux/userSlice";
import { Spin, Tag, Table } from "../../Components/UI";
import { DownloadOutlined, ArrowLeftOutlined, CalendarOutlined, FileTextOutlined } from "../../Components/UI/Icons";
import {
  Box, Flex, Heading, Text, Image, VStack, HStack,
  Badge, Divider, SimpleGrid, Button, Tabs, TabList,
  Tab, TabPanels, TabPanel, Stat, StatLabel, StatNumber,
} from "@chakra-ui/react";
import { LOGO_BASE64, BANNER_BASE64 } from "./logoAssets";
import WingCoordinatorDoc from "./Documents/WingCoordinatorDoc";
import { getAllTimes, getUserId } from "../../Utils/auth";
import { recordFileDownloadAudit, recordViewAudit } from "../../Utils/auditLogger";

// ── score helpers ─────────────────────────────────────────────────────────────
const form1Score = (form) => {
  if (!form) return { score: 0, total: 0 };
  const keys = Object.keys(form).filter(k => !["totalScore","OutOf","ObservationDates"].includes(k));
  const vals = keys.map(k => form[k]);
  const score = vals.reduce((s, v) => s + (v === "Yes" ? 1 : v === "Sometimes" ? 0.5 : 0), 0);
  const total = vals.filter(v => ["Yes","No","Sometimes","N/A"].includes(v)).length;
  return { score, total };
};

const f2SectionScore = (items = []) => {
  let score = 0, total = 0;
  items.forEach(i => {
    if (["1","2","3","4"].includes(i.answer)) { score += parseInt(i.answer); total += 4; }
    else if (i.answer !== "N/A") total += 4;
  });
  return { score, total };
};

// ── badge util ────────────────────────────────────────────────────────────────
const answerColor = (v) =>
  v === "Yes" ? "green" : v === "No" ? "red" : v === "Sometimes" ? "orange" : v === "N/A" ? "gray" : "default";

// ── inline components ─────────────────────────────────────────────────────────
const MetaCard = ({ label, value }) => (
  <Box>
    <Text fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb="2px">{label}</Text>
    <Text fontSize="sm" fontWeight="600" color="brand.text">{value || "—"}</Text>
  </Box>
);

const SectionHeading = ({ children, count, colorScheme = "green" }) => (
  <Flex align="center" justify="space-between" mb={3} mt={6}>
    <Heading size="sm" color="brand.text">{children}</Heading>
    {count !== undefined && (
      <Badge colorScheme={colorScheme} borderRadius="full" px={3} fontSize="xs">{count} records</Badge>
    )}
  </Flex>
);

// ── Form 1 card ──────────────────────────────────────────────────────────────
const Form1Card = ({ item, index }) => {
  const tScore = form1Score(item.teacherForm);
  const oScore = form1Score(item.observerForm);
  return (
    <Flex
      bg="green.50" borderRadius="lg" borderWidth="1px" borderColor="green.100"
      borderLeftWidth="4px" borderLeftColor="green.400"
      boxShadow="xs" px={4} py={3} mb={2} align="center" justify="space-between"
      flexWrap="wrap" gap={2}
    >
      <HStack spacing={3} flex={1} minW={0}>
        <Badge colorScheme="green" fontSize="10px" flexShrink={0}>#{index + 1}</Badge>
        <Box minW={0}>
          <Text fontWeight="600" fontSize="sm" color="green.900" isTruncated>
            {item.teacherID?.name ?? "—"}
          </Text>
          <Text fontSize="xs" color="green.700">
            {item.className}/{item.section} · {getAllTimes(item.date)?.formattedDate2} · Obs: {item.userId?.name ?? "—"}
          </Text>
        </Box>
      </HStack>
      <HStack spacing={4} flexShrink={0}>
        <Box textAlign="right">
          <Text fontSize="10px" color="green.600">Teacher Score</Text>
          <Text fontWeight="700" fontSize="sm" color="green.700">{tScore.score}/{tScore.total}</Text>
        </Box>
        <Box textAlign="right">
          <Text fontSize="10px" color="green.600">Observer Score</Text>
          <Text fontWeight="700" fontSize="sm" color="green.700">{oScore.score}/{oScore.total}</Text>
        </Box>
      </HStack>
    </Flex>
  );
};

// ── Form 2 card ──────────────────────────────────────────────────────────────
const F2_SECTIONS = [
  { key: "essentialAggrements",   label: "Essential Agreements" },
  { key: "planingAndPreparation", label: "Planning & Preparation" },
  { key: "classRoomEnvironment",  label: "Classroom Environment" },
  { key: "instruction",           label: "Instruction" },
];

const Form2Card = ({ item, index }) => (
  <Box bg="blue.50" borderRadius="lg" borderWidth="1px" borderColor="blue.100"
    borderLeftWidth="4px" borderLeftColor="blue.400"
    boxShadow="xs" mb={2} overflow="hidden">
    <Flex px={4} py={3} align="center" justify="space-between" flexWrap="wrap" gap={2}>
      <HStack spacing={3} flex={1} minW={0}>
        <Badge colorScheme="blue" fontSize="10px" flexShrink={0}>#{index + 1}</Badge>
        <Box minW={0}>
          <Text fontWeight="600" fontSize="sm" color="blue.900" isTruncated>
            {item.grenralDetails?.NameoftheVisitingTeacher?.name ?? "—"}
          </Text>
          <Text fontSize="xs" color="blue.700">
            {item.grenralDetails?.className}/{item.grenralDetails?.Section} · {getAllTimes(item.grenralDetails?.DateOfObservation)?.formattedDate2} · {item.grenralDetails?.Subject ?? ""} · Obs: {item.createdBy?.name ?? "—"}
          </Text>
        </Box>
      </HStack>
      <Badge colorScheme="blue" fontSize="xs" px={2} py={1} borderRadius="full" flexShrink={0}>
        {item.totalScores}/{item.scoreOutof}
      </Badge>
    </Flex>
    {(item.ObserverFeedback?.length > 0 || item.TeacherFeedback?.length > 0) && (
      <Box px={4} pb={3} borderTopWidth="1px" borderTopColor="blue.100" pt={2}>
        {item.ObserverFeedback?.length > 0 && item.ObserverFeedback.map((f, i) => (
          <Flex key={i} gap={2} mb={1}>
            <Text fontSize="xs" color="blue.500" flexShrink={0} fontWeight="600">Obs:</Text>
            <Text fontSize="xs" color="blue.900">{f.answer}</Text>
          </Flex>
        ))}
        {item.TeacherFeedback?.length > 0 && item.TeacherFeedback.map((f, i) => (
          <Flex key={i} gap={2} mb={1}>
            <Text fontSize="xs" color="blue.500" flexShrink={0} fontWeight="600">Teacher:</Text>
            <Text fontSize="xs" color="blue.900">{f.answer}</Text>
          </Flex>
        ))}
      </Box>
    )}
  </Box>
);

// ── Form 5 card ──────────────────────────────────────────────────────────────
const Form5Card = ({ item, index }) => (
  <Box bg="teal.50" borderRadius="lg" borderWidth="1px" borderColor="teal.100"
    borderLeftWidth="4px" borderLeftColor="teal.400"
    boxShadow="xs" mb={2} overflow="hidden">
    <Flex px={4} py={3} align="center" justify="space-between" flexWrap="wrap" gap={2}>
      <HStack spacing={3} flex={1} minW={0}>
        <Badge colorScheme="teal" fontSize="10px" flexShrink={0}>#{index + 1}</Badge>
        <Box minW={0}>
          <Text fontWeight="600" fontSize="sm" color="teal.900" isTruncated>
            {item.grenralDetails?.NameoftheVisitingTeacher?.name ?? item.createdBy?.name ?? "—"}
          </Text>
          <Text fontSize="xs" color="teal.700">
            {item.grenralDetails?.className}/{item.grenralDetails?.Section} · {getAllTimes(item.grenralDetails?.DateOfObservation)?.formattedDate2} · {item.grenralDetails?.Subject ?? ""} · Obs: {item.createdBy?.name ?? "—"}
          </Text>
        </Box>
      </HStack>
      <Badge colorScheme="teal" fontSize="xs" px={2} py={1} borderRadius="full" flexShrink={0}>
        {item.percentageScore ? `${item.percentageScore}%` : "—"}
      </Badge>
    </Flex>
  </Box>
);

// ── Form 3 card ──────────────────────────────────────────────────────────────
const f3Score = (formName) => {
  let score = 0, total = 0;
  const keys = ["maintenanceOfNotebooks", "qualityOfOppurtunities", "qualityOfTeacherFeedback", "qualityOfLearner"];
  keys.forEach((k) => {
    if (formName?.[k]) {
      formName[k].forEach((i) => {
        if (["1", "2", "3"].includes(i?.answer)) { score += parseInt(i.answer, 10); total += 3; }
      });
    }
  });
  return { score, total };
};

const Form3Card = ({ item, index }) => {
  const tScore = f3Score(item.TeacherForm);
  const oScore = f3Score(item.ObserverForm);
  return (
    <Box bg="purple.50" borderRadius="lg" borderWidth="1px" borderColor="purple.100"
      borderLeftWidth="4px" borderLeftColor="purple.400"
      boxShadow="xs" mb={2} overflow="hidden">
      <Flex px={4} py={3} align="center" justify="space-between" flexWrap="wrap" gap={2}>
        <HStack spacing={3} flex={1} minW={0}>
          <Badge colorScheme="purple" fontSize="10px" flexShrink={0}>#{index + 1}</Badge>
          <Box minW={0}>
            <Text fontWeight="600" fontSize="sm" color="purple.900" isTruncated>
              {item.teacherID?.name || item.createdBy?.name || "—"}
            </Text>
            <Text fontSize="xs" color="purple.700">
              {item.grenralDetails?.className}/{item.grenralDetails?.Section} · {getAllTimes(item.grenralDetails?.DateOfObservation)?.formattedDate2} · {item.grenralDetails?.Subject ?? ""} · Obs: {item.grenralDetails?.NameofObserver?.name || "—"}
            </Text>
          </Box>
        </HStack>
        <HStack spacing={4} flexShrink={0}>
          <Box textAlign="right">
            <Text fontSize="10px" color="purple.500">Teacher Score</Text>
            <Text fontWeight="700" fontSize="sm" color="purple.700">{tScore.score}/{tScore.total}</Text>
          </Box>
          <Box textAlign="right">
            <Text fontSize="10px" color="purple.500">Observer Score</Text>
            <Text fontWeight="700" fontSize="sm" color="purple.700">{oScore.score}/{oScore.total}</Text>
          </Box>
        </HStack>
      </Flex>
      {(item.observerFeedback || item.teacherReflationFeedback) && (
        <Box px={4} pb={3} borderTopWidth="1px" borderTopColor="purple.100" pt={2}>
          {item.observerFeedback && (
            <Flex gap={2} mb={1}>
              <Text fontSize="xs" color="purple.500" flexShrink={0} fontWeight="600">Obs Feedback:</Text>
              <Text fontSize="xs" color="purple.900">{item.observerFeedback}</Text>
            </Flex>
          )}
          {item.teacherReflationFeedback && (
            <Flex gap={2}>
              <Text fontSize="xs" color="purple.500" flexShrink={0} fontWeight="600">Reflection:</Text>
              <Text fontSize="xs" color="purple.900">{item.teacherReflationFeedback}</Text>
            </Flex>
          )}
        </Box>
      )}
    </Box>
  );
};

// ── Main Report Page ──────────────────────────────────────────────────────────
function WingCoordinatorReport() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const load = async () => {
      const res = await dispatch(GetSingleWingFrom(id));
      if (res?.payload?.success) {
        setData(res.payload.data);
        recordViewAudit({
          module: "Wing Coordinator Report",
          targetId: id,
          details: `Viewed Wing Coordinator Report (ID: ${id})`,
        });
      }
      setLoading(false);
    };
    load();
  }, [dispatch, id]);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const blob = await ReactPDF.pdf(<WingCoordinatorDoc data={data} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const pdfName = `Wing-Coordinator-Report-${id}.pdf`;
      link.download = pdfName;
      link.click();
      URL.revokeObjectURL(url);

      recordFileDownloadAudit({
        fileName: pdfName,
        fileType: "PDF",
        module: "Wing Coordinator Report",
        extraDetails: `Report ID: ${id}`,
      });
    } finally {
      setDownloading(false);
    }
  };

  const fmt = (d) => getAllTimes(d)?.formattedDate2 ?? "—";
  const dateRange = data?.range?.length === 2 ? `${fmt(data.range[0])} – ${fmt(data.range[1])}` : "—";
  const classes = Array.isArray(data?.className) ? data.className.join(", ") : data?.className ?? "—";
  const observerName = data?.userId?.name || getUserId()?.name || "—";

  const FORM_TABS = [
    { key: "form1", label: "Fortnightly Monitor",       color: "green"  },
    { key: "form2", label: "Classroom Walkthrough",     color: "blue"   },
    { key: "form3", label: "Notebook Checking",         color: "purple" },
    { key: "form5", label: "Co-Scholastic Obs",         color: "teal"   },
    { key: "form4", label: "Learning Progress",         color: "orange" },
  ];

  return (
    <Box p={{ base: 4, md: 8 }} minH="calc(100vh - 72px)" bg="brand.background">
      <Box maxW="1200px" mx="auto">

        {/* Header */}
        <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
          <HStack spacing={3}>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeftOutlined />}
              color="gray.500"
              _hover={{ color: "brand.text", bg: "white" }}
              onClick={() => navigate("/wing-coordinator")}
            >
              Back
            </Button>
            <Box>
              <Heading size="lg" color="brand.text">Wing Coordinator Report</Heading>
              <Text color="gray.500" fontSize="sm">{dateRange} · {classes} · Observer: {observerName}</Text>
            </Box>
          </HStack>
          <Button
            leftIcon={<DownloadOutlined />}
            bg="brand.primary"
            color="white"
            _hover={{ bg: "brand.secondary" }}
            onClick={downloadPDF}
            isLoading={downloading}
            loadingText="Generating PDF…"
            size="md"
            isDisabled={loading || !data}
          >
            Download PDF
          </Button>
        </Flex>

        {loading ? (
          <Flex justify="center" align="center" h="400px">
            <Spin size="large" />
          </Flex>
        ) : !data ? (
          <Box textAlign="center" py={20}><Text color="gray.400">Report not found.</Text></Box>
        ) : (
          <>
            {/* Logo banner */}
            <Flex justify="center" align="center" gap={6} bg="white" p={5} borderRadius="2xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100" mb={6}>
              <Image src={LOGO_BASE64} h="70px" w="auto" alt="Logo" />
              <Image src={BANNER_BASE64} h="50px" w="auto" alt="Banner" />
            </Flex>

            {/* Summary stats */}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
              {FORM_TABS.map(({ key, label, color }) => (
                <Box key={key} bg="white" borderRadius="xl" p={4} boxShadow="sm" borderWidth="1px" borderColor="gray.100" textAlign="center">
                  <Text fontSize="xs" color="gray.500" mb={1}>{label}</Text>
                  <Text fontSize="2xl" fontWeight="800" color={`${color}.500`}>{data[key]?.length ?? 0}</Text>
                  <Text fontSize="xs" color="gray.400">records</Text>
                </Box>
              ))}
            </SimpleGrid>

            {/* Meta info */}
            <Box bg="white" borderRadius="2xl" p={5} boxShadow="sm" borderWidth="1px" borderColor="gray.100" mb={6}>
              <Text fontWeight="700" color="brand.text" mb={3}>Report Details</Text>
              <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
                <MetaCard label="Observer" value={observerName} />
                <MetaCard label="Date Range" value={dateRange} />
                <MetaCard label="Classes" value={classes} />
                <MetaCard label="Created" value={fmt(data.createdAt)} />
                <MetaCard label="Status" value={data.isComplete ? "Completed" : "Draft"} />
              </SimpleGrid>
            </Box>

            {/* Monthly Report */}
            {data.monthlyReport?.length > 0 && (
              <Box bg="white" borderRadius="2xl" p={5} boxShadow="sm" borderWidth="1px" borderColor="gray.100" mb={6}>
                <SectionHeading>Monthly Report</SectionHeading>
                <VStack spacing={3} align="stretch">
                  {data.monthlyReport.map((item, i) => {
                    const fileList = (
                      Array.isArray(item?.files)
                        ? item.files
                        : item?.files && typeof item.files === "object"
                        ? Object.values(item.files)
                        : []
                    ).filter(Boolean);

                    return (
                    <Box key={i} bg="brand.background" p={4} borderRadius="lg" borderWidth="1px" borderColor="gray.100">
                      <Text fontSize="xs" color="gray.500" mb={1}>{item.question}</Text>
                      {item.type === "text" ? (
                        <Text fontSize="sm" color="brand.text">{item.answer || <Text as="span" color="gray.400" fontStyle="italic">No answer provided</Text>}</Text>
                      ) : (
                        <Box mt={2} overflowX="auto" bg="white" p={2} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                          {item.tableData?.filter(Boolean)?.length > 0 ? (
                            <Table
                              size="small"
                              pagination={false}
                              dataSource={item.tableData.filter(Boolean)}
                              rowKey={(_, idx) => idx}
                              columns={[
                                {
                                  title: "S.No.",
                                  dataIndex: "sNo",
                                  key: "sNo",
                                  width: "60px",
                                  render: (_, __, idx) => idx + 1,
                                },
                                ...(item.columns || []).map((col, cIdx) => ({
                                  title: col,
                                  dataIndex: `col_${cIdx}`,
                                  key: `col_${cIdx}`,
                                  render: (val) => {
                                    if (typeof val === "boolean") {
                                      return val ? (
                                        <span style={{ color: "#4A6741", fontWeight: "bold", fontSize: "15px" }}>✔️</span>
                                      ) : (
                                        <span style={{ color: "#CBD5E0", fontSize: "15px" }}>—</span>
                                      );
                                    }
                                    return val || "—";
                                  }
                                }))
                              ]}
                            />
                          ) : (
                            <Text fontSize="sm" color="gray.400" fontStyle="italic">No table data provided</Text>
                          )}
                        </Box>
                      )}

                      {fileList.length > 0 && (
                        <Box mt={4} pt={3} borderTopWidth="1px" borderTopColor="gray.200">
                          <Text fontSize="xs" fontWeight="700" color="gray.600" mb={3}>
                            Attached PDF Documents:
                          </Text>
                          <VStack spacing={4} align="stretch">
                            {fileList.map((file, fIdx) => {
                              const isPdf =
                                file.type === "application/pdf" ||
                                file.url?.startsWith("data:application/pdf") ||
                                file.name?.toLowerCase().endsWith(".pdf");

                              if (isPdf && file.url) {
                                return (
                                  <Box
                                    key={fIdx}
                                    borderRadius="xl"
                                    overflow="hidden"
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    bg="white"
                                    boxShadow="sm"
                                  >
                                    <Flex
                                      align="center"
                                      justify="space-between"
                                      bg="gray.50"
                                      px={4}
                                      py={2.5}
                                      borderBottomWidth="1px"
                                      borderBottomColor="gray.200"
                                    >
                                      <Text fontSize="xs" fontWeight="600" color="brand.text">
                                        📄 {file.name || `Uploaded PDF Document #${fIdx + 1}`}
                                      </Text>
                                      <HStack spacing={2}>
                                        <Button
                                          size="xs"
                                          colorScheme="teal"
                                          onClick={() => {
                                            const link = document.createElement("a");
                                            link.href = file.url;
                                            const dlName = file.name || `document_${fIdx + 1}.pdf`;
                                            link.download = dlName;
                                            link.click();
                                            recordFileDownloadAudit({
                                              fileName: dlName,
                                              fileType: "Attachment PDF",
                                              module: "Wing Coordinator Report (Attachment)",
                                            });
                                          }}
                                        >
                                          Download PDF
                                        </Button>
                                        <Button
                                          size="xs"
                                          variant="outline"
                                          colorScheme="blue"
                                          onClick={() => {
                                            const win = window.open();
                                            if (win) {
                                              win.document.write(
                                                `<iframe src="${file.url}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>`
                                              );
                                            }
                                          }}
                                        >
                                          Open Fullscreen
                                        </Button>
                                      </HStack>
                                    </Flex>
                                    <Box position="relative" w="100%" h="600px" bg="gray.100">
                                      <iframe
                                        src={file.url}
                                        title={file.name || `PDF Viewer ${fIdx + 1}`}
                                        width="100%"
                                        height="100%"
                                        style={{ border: "none" }}
                                      />
                                    </Box>
                                  </Box>
                                );
                              }

                              const isImage =
                                file.url?.startsWith("data:image/") || file.type?.startsWith("image/");
                              if (isImage) {
                                return (
                                  <Box
                                    key={fIdx}
                                    p={3}
                                    bg="white"
                                    borderRadius="lg"
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    maxW="350px"
                                  >
                                    <Image
                                      src={file.url}
                                      maxH="240px"
                                      w="auto"
                                      objectFit="contain"
                                      borderRadius="md"
                                      alt={file.name}
                                    />
                                    <Text fontSize="xs" color="gray.600" mt={2} fontWeight="500">
                                      {file.name}
                                    </Text>
                                  </Box>
                                );
                              }

                              return (
                                <Badge
                                  key={fIdx}
                                  px={3}
                                  py={1.5}
                                  colorScheme="teal"
                                  borderRadius="md"
                                  cursor="pointer"
                                  fontSize="xs"
                                  display="inline-flex"
                                  alignItems="center"
                                  gap={1}
                                  onClick={() => {
                                    if (file.url) {
                                      const link = document.createElement("a");
                                      link.href = file.url;
                                      const dlName = file.name || `attachment_${fIdx + 1}`;
                                      link.download = dlName;
                                      link.click();
                                      recordFileDownloadAudit({
                                        fileName: dlName,
                                        fileType: "Attachment File",
                                        module: "Wing Coordinator Report (Attachment)",
                                      });
                                    }
                                  }}
                                >
                                  📎 {file.name || `Attachment #${fIdx + 1}`}
                                </Badge>
                              );
                            })}
                          </VStack>
                        </Box>
                      )}
                    </Box>
                    );
                  })}
                </VStack>
              </Box>
            )}

            {/* Form tabs */}
            <Box bg="white" borderRadius="2xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100" overflow="hidden" mb={6}>
              <Tabs variant="line" colorScheme="green">
                <TabList px={4} pt={3} borderBottomWidth="1px" borderBottomColor="gray.100">
                  {FORM_TABS.map(({ key, label, color }) => (
                    <Tab key={key} fontSize="sm" fontWeight="500" _selected={{ color: "brand.primary", borderBottomColor: "brand.primary" }}>
                      {label}
                      <Badge ml={2} colorScheme={color} borderRadius="full" fontSize="10px">{data[key]?.length ?? 0}</Badge>
                    </Tab>
                  ))}
                </TabList>
                <TabPanels>
                  {/* Form 1 */}
                  <TabPanel p={4}>
                    {data.form1?.length === 0 ? (
                      <Box textAlign="center" py={10}><Text color="gray.400" fontSize="sm">No Fortnightly Monitor forms linked.</Text></Box>
                    ) : (
                      data.form1.map((item, i) => <Form1Card key={item._id} item={item} index={i} />)
                    )}
                  </TabPanel>

                  {/* Form 2 */}
                  <TabPanel p={4}>
                    {data.form2?.length === 0 ? (
                      <Box textAlign="center" py={10}><Text color="gray.400" fontSize="sm">No Classroom Walkthrough forms linked.</Text></Box>
                    ) : (
                      data.form2.map((item, i) => <Form2Card key={item._id} item={item} index={i} />)
                    )}
                  </TabPanel>

                  {/* Form 3 */}
                  <TabPanel p={4}>
                    {data.form3?.length === 0 ? (
                      <Box textAlign="center" py={10}><Text color="gray.400" fontSize="sm">No Notebook Checking Proforma forms linked.</Text></Box>
                    ) : (
                      data.form3.map((item, i) => <Form3Card key={item._id} item={item} index={i} />)
                    )}
                  </TabPanel>

                  {/* Form 5 */}
                  <TabPanel p={4}>
                    {data.form5?.length === 0 ? (
                      <Box textAlign="center" py={10}><Text color="gray.400" fontSize="sm">No Co-Scholastic Classroom Observation forms linked.</Text></Box>
                    ) : (
                      data.form5?.map((item, i) => <Form5Card key={item._id} item={item} index={i} />)
                    )}
                  </TabPanel>

                  {/* Form 4 */}
                  <TabPanel p={4}>
                    {data.form4?.length === 0 ? (
                      <Box textAlign="center" py={10}><Text color="gray.400" fontSize="sm">No Learning Progress Checklist forms linked.</Text></Box>
                    ) : (
                      <Table
                        dataSource={data.form4}
                        rowKey="_id"
                        size="small"
                        pagination={false}
                        columns={[
                          { title: "Teacher", dataIndex: ["teacherId","name"], key: "t", render: (v, r) => v || r.userId?.name || "—" },
                          { title: "Date", dataIndex: "createdAt", key: "d", render: d => fmt(d) },
                        ]}
                      />
                    )}
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>

            {/* PDF Preview */}
            <Box bg="white" borderRadius="2xl" p={5} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
              <Flex align="center" justify="space-between" mb={4}>
                <Heading size="md" color="brand.text">PDF Preview</Heading>
                <Text fontSize="xs" color="gray.400">Scroll to see all pages</Text>
              </Flex>
              <Box borderRadius="xl" overflow="hidden" borderWidth="1px" borderColor="gray.200">
                {data && (
                  <PDFViewer key={id} style={{ width: "100%", height: "800px", border: "none" }}>
                    <WingCoordinatorDoc data={data} />
                  </PDFViewer>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

export default WingCoordinatorReport;
